import { mapActions, mapGetters } from 'vuex';
import { pendingPotential, originalPotential, recalculatedDetail, bonusFigures, fairInfo, freezeCost, isFreezeActive, isFreezeOverridden } from '@/utils/betFreezeDisplay';
import { BET_FREEZE_MAX_AUTO_REPRICES } from '@/store/modules/betFreeze';

const closedConfirm = () => ({legUniqueId: null, busy: false, refreshing: false, message: null, messageType: 'info', autoReprices: 0});

/**
 * betFreeze in a ticket detail: per-leg quotes, confirmation panel with the
 * quote countdown, silent re-pricing, execution. Shared by the classic
 * MyBets and the American BetDetailModal so both show the same prices and
 * follow the same flow. Every rule is enforced server-side (rocky-bets): the
 * UI renders what the preview says and never computes a price itself.
 *
 * The host component provides:
 *   - `betToShow`                   the bet open in the detail (data or computed)
 *   - `isPlayerEligibleForCashout`  same audience as the cashout
 *   - `_reloadBetsAfterBetFreeze(code)`  reload the bets and show the fresh one
 * and may override `formatBetFreezeMoney(bet, amount)` (default "12.34 EUR").
 */
export default {
  data() {
    return {
      // Leg whose confirmation panel is open + feedback
      betFreezeConfirm: closedConfirm(),
      betFreezeNotice: null,
      // Ticks every second while a confirmation panel is open (countdown).
      betFreezeNow: Date.now(),
    };
  },

  computed: {
    ...mapGetters({
      getBetFreeze: 'settings/getBetFreeze',
      getBetFreezePreview: 'betFreeze/getBetFreezePreview',
    }),
    // betFreeze is asked to the server only for pending multiples of a
    // player on a skin with the feature on; the server enforces everything.
    isBetFreezeCandidate() {
      return this.getBetFreeze === true
        && Boolean(this.betToShow)
        && this.betToShow.result === 'pending'
        && this.betToShow.type === 'multiple'
        && this.isPlayerEligibleForCashout;
    },
    // How many selections of the open bet can still be frozen (banner).
    betFreezeAvailableCount() {
      return this._betFreezeAvailableCount();
    },
  },

  created() {
    this._betFreezeClock = null;
  },

  beforeDestroy() {
    this._stopBetFreezeClock();
  },

  methods: {
    ...mapActions({
      previewBetFreeze: 'betFreeze/previewBetFreeze',
      executeBetFreeze: 'betFreeze/executeBetFreeze',
      checkBetFreezeAvailability: 'betFreeze/checkBetFreezeAvailability',
    }),

    /** Detail opened on `bet`: reset the panel and fetch fresh quotes. */
    _onBetFreezeDetailOpened(bet) {
      this.closeBetFreezeConfirm();
      this.betFreezeNotice = null;
      if (this.isBetFreezeCandidate && bet && bet.code) {
        this.previewBetFreeze({ code: bet.code });
      }
    },

    // Freezes still allowed by the tier, capped by the legs freezable right now.
    _betFreezeAvailableCount() {
      if (!this.isBetFreezeCandidate || !this.betToShow) return 0;
      const preview = this.getBetFreezePreview(this.betToShow.code);
      if (preview.status !== 'ready') return 0;
      const freezable = (this.betToShow.odds || []).filter(odd => {
        const leg = this.betFreezeLegState(odd);
        return leg && leg.freezable;
      }).length;
      const remaining = Number(preview.maxFreezes) - Number(preview.frozenCount || 0);
      if (!Number.isFinite(remaining) || remaining <= 0) return 0;
      return Math.min(remaining, freezable);
    },

    /**
     * Preview entry of an open leg, or null when nothing must be shown:
     * no preview, leg frozen/settled, or a permanent refusal (the button is
     * only rendered, disabled, for refusals that may clear by themselves).
     */
    betFreezeLegState(odd) {
      if (!this.isBetFreezeCandidate || !odd || odd.betFreeze || odd.is_bet_builder) return null;
      const preview = this.getBetFreezePreview(this.betToShow.code);
      if (preview.status !== 'ready') return null;
      const leg = preview.legs[String(odd.unique_id)];
      if (!leg) return null;
      if (leg.freezable || leg.temporary) return leg;
      return null;
    },

    betFreezeReasonText(reason) {
      const key = `betFreeze.reasons.${reason}`;
      return this.$te(key) ? this.$t(key) : this.$t('betFreeze.reasons.DEFAULT');
    },

    /**
     * Opens the confirmation panel with a FRESH quote: the validity window
     * (quoteValiditySeconds) starts when the player actually reads the
     * summary, not when the ticket detail was opened.
     */
    async openBetFreezeConfirm(odd) {
      this.betFreezeNotice = null;
      const legUniqueId = String(odd.unique_id);
      this.betFreezeConfirm = {...closedConfirm(), legUniqueId, refreshing: true};
      this._startBetFreezeClock();
      await this.previewBetFreeze({ code: this.betToShow.code, silent: true });
      if (this.betFreezeConfirm.legUniqueId !== legUniqueId) return; // closed meanwhile
      this.betFreezeConfirm.refreshing = false;
      const fresh = this.betFreezeLegState(odd);
      if (!fresh || !fresh.freezable) {
        this.closeBetFreezeConfirm();
        this.betFreezeNotice = {type: 'warning', text: this.$t('betFreeze.messages.noLongerAvailable')};
      }
    },

    closeBetFreezeConfirm() {
      this.betFreezeConfirm = closedConfirm();
      this._stopBetFreezeClock();
    },

    // Quote expiry in ms on this device's clock (store: localQuoteExpiry).
    _betFreezeExpiryMs(leg) {
      if (!leg) return null;
      if (leg.localExpiresAt) return leg.localExpiresAt;
      const serverExpiry = leg.expiresAt ? new Date(leg.expiresAt).getTime() : NaN;
      return Number.isFinite(serverExpiry) ? serverExpiry : null;
    },

    // Whole seconds left on the quote of an open panel, null when unknown.
    betFreezeSecondsLeft(odd) {
      const expiry = this._betFreezeExpiryMs(this.betFreezeLegState(odd));
      if (!expiry) return null;
      return Math.max(0, Math.ceil((expiry - this.betFreezeNow) / 1000));
    },

    _startBetFreezeClock() {
      this._stopBetFreezeClock();
      this.betFreezeNow = Date.now();
      this._betFreezeClock = setInterval(() => {
        this.betFreezeNow = Date.now();
        this._autoRepriceIfExpired()
          .catch(e => console.error('[betFreeze] automatic re-price failed', e));
      }, 1000);
    },

    _stopBetFreezeClock() {
      if (this._betFreezeClock) {
        clearInterval(this._betFreezeClock);
        this._betFreezeClock = null;
      }
    },

    /**
     * Quote expired while the panel is open and the player did nothing:
     * fetch a new price silently. A changed frozen odd is flagged so the
     * player never confirms a price they have not seen.
     *
     * Never while the tab is hidden, and at most BET_FREEZE_MAX_AUTO_REPRICES
     * times in a row: after that the countdown stays at 0 and Confirm fetches
     * the new price (confirmBetFreeze), so an idle panel stops polling.
     */
    async _autoRepriceIfExpired() {
      const c = this.betFreezeConfirm;
      if (!c.legUniqueId || c.busy || c.refreshing || !this.betToShow) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      if ((c.autoReprices || 0) >= BET_FREEZE_MAX_AUTO_REPRICES) return;
      const odd = (this.betToShow.odds || []).find(o => String(o.unique_id) === c.legUniqueId);
      if (!odd || this.betFreezeSecondsLeft(odd) !== 0) return;
      const previousOdd = this.betFreezeLegState(odd) && this.betFreezeLegState(odd).frozenOdd;
      c.autoReprices = (c.autoReprices || 0) + 1;
      c.refreshing = true;
      await this.previewBetFreeze({ code: this.betToShow.code, silent: true });
      if (this.betFreezeConfirm.legUniqueId !== c.legUniqueId) return;
      this.betFreezeConfirm.refreshing = false;
      const fresh = this.betFreezeLegState(odd);
      if (!fresh || !fresh.freezable) {
        this.closeBetFreezeConfirm();
        this.betFreezeNotice = {type: 'warning', text: this.$t('betFreeze.messages.noLongerAvailable')};
      } else if (previousOdd != null && fresh.frozenOdd !== previousOdd) {
        this.betFreezeConfirm.message = this.$t('betFreeze.messages.priceChanged');
        this.betFreezeConfirm.messageType = 'warning';
      }
    },

    // Never throws: the freeze is already decided, a failed refresh is logged
    // and the notice on screen stays the one of the freeze.
    async _refreshAfterBetFreeze(code) {
      try {
        await this.checkBetFreezeAvailability({ code, force: true });
        await this._reloadBetsAfterBetFreeze(code);
        if (this.isBetFreezeCandidate) await this.previewBetFreeze({ code });
      } catch (e) {
        console.error('[betFreeze] refresh after the freeze failed', code, e);
      }
    },

    // After a refused/expired quote: fetch a new price and keep the panel
    // open if the leg is still freezable, otherwise close it with a notice.
    async _repriceBetFreeze(code, odd, messageKey) {
      await this.previewBetFreeze({ code, silent: true });
      this.betFreezeConfirm.busy = false;
      const refreshed = this.betFreezeLegState(odd);
      if (refreshed && refreshed.freezable) {
        this.betFreezeConfirm.message = this.$t(messageKey);
        this.betFreezeConfirm.messageType = 'warning';
      } else {
        this.closeBetFreezeConfirm();
        this.betFreezeNotice = {type: 'warning', text: this.$t('betFreeze.messages.noLongerAvailable')};
      }
    },

    async confirmBetFreeze(odd) {
      const code = this.betToShow.code;
      const leg = this.betFreezeLegState(odd);
      if (!leg || !leg.freezable) return;
      this.betFreezeConfirm.busy = true;
      this.betFreezeConfirm.message = null;
      // A player action: the automatic re-pricing may start again.
      this.betFreezeConfirm.autoReprices = 0;

      try {
        // Quote about to expire: refresh the price and ask again rather than
        // sending a request the server would refuse.
        const expiry = this._betFreezeExpiryMs(leg);
        if (!expiry || expiry - Date.now() < 1000) {
          await this._repriceBetFreeze(code, odd, 'betFreeze.messages.priceRefreshed');
          return;
        }

        const res = await this.executeBetFreeze({ code, legUniqueId: String(odd.unique_id), quoteId: leg.quoteId });
        if (res.success) {
          this.closeBetFreezeConfirm();
          this.betFreezeNotice = {type: 'success', text: this.$t('betFreeze.messages.done')};
          await this._refreshAfterBetFreeze(code);
          return;
        }

        if (['BET_FREEZE_PRICE_CHANGED', 'BET_FREEZE_QUOTE_EXPIRED', 'BET_FREEZE_QUOTE_OUTDATED'].includes(res.errorCode)) {
          await this._repriceBetFreeze(code, odd, 'betFreeze.messages.priceChanged');
          return;
        }

        this.closeBetFreezeConfirm();
        this.betFreezeNotice = res.outcomeUnknown
          ? {type: 'warning', text: this.$t('betFreeze.messages.outcomeUnknown')}
          : {type: 'error', text: this.betFreezeReasonText(res.errorCode)};
        await this._refreshAfterBetFreeze(code);
      } catch (e) {
        // The store actions do not throw: reaching this is a bug. Never leave
        // the panel stuck on its spinner — the bet list shows the real state.
        console.error('[betFreeze] confirm failed', code, e);
        this.closeBetFreezeConfirm();
        this.betFreezeNotice = {type: 'error', text: this.betFreezeReasonText('DEFAULT')};
      }
    },

    // --- Display ------------------------------------------------------------

    /** Money as the host shows it; the classic console writes "12.34 EUR". */
    formatBetFreezeMoney(bet, amount) {
      return `${amount.toFixed(2)} ${bet.currency}`;
    },

    _pendingPotential(bet) {
      return pendingPotential(bet);
    },

    // Placement-time potential (struck through) of a bet recalculated after a
    // betFreeze or a void / half leg, or null.
    betFreezeOriginalWinning(bet) {
      const original = originalPotential(bet);
      return original === null ? null : this.formatBetFreezeMoney(bet, original);
    },

    // Caption of that struck figure: "before freeze" on a frozen bet,
    // "at placement" when a void / half leg changed it.
    originalWinningLabel(bet) {
      return bet && bet.betFreeze ? this.$t('betFreeze.beforeFreeze') : this.$t('bets.atPlacement');
    },

    // True when the placement figures (`_multiple_detail`) no longer apply.
    hasRecalculatedFigures(bet) {
      return recalculatedDetail(bet) !== null;
    },

    // Multiple bonus as the player gets it today, "4.80 EUR (3%)", and the
    // placement one struck through when a void / frozen leg changed it.
    // `shown`: there is or was a bonus. Null for integral / system bets.
    bonusDisplay(bet) {
      const figures = bonusFigures(bet);
      if (!figures) return null;
      const format = ({bonus, percentage}) => this.formatBetFreezeMoney(bet, bonus) + (percentage > 0 ? ` (${percentage}%)` : '');
      return {
        current: format(figures.current),
        original: figures.original ? format(figures.original) : null,
        shown: figures.current.bonus > 0 || Boolean(figures.original && figures.original.bonus > 0)
      };
    },

    // Potential winnings before the freeze being confirmed, struck through
    // next to the new estimate (= placement-time figure on the first freeze).
    betFreezeCurrentWinning(bet) {
      if (!bet || (bet.type !== 'multiple' && bet.type !== 'single') || !bet._multiple_detail) return null;
      const current = pendingPotential(bet);
      return Number.isFinite(current) ? this.formatBetFreezeMoney(bet, current) : null;
    },

    // Frozen leg still paying its frozen odd / frozen leg voided with the bet.
    isLegFrozen(odd) {
      return isFreezeActive(odd);
    },
    isLegFreezeOverridden(odd) {
      return isFreezeOverridden(odd);
    },
    // Why the freeze of a leg was taken back: the operator decided the leg (LEG_DEFINED, the bet
    // goes on) or the whole bet was voided / cancelled.
    legFreezeOverriddenLabel(odd) {
      const o = odd && odd.betFreeze && odd.betFreeze.overridden;
      return o && o.operation === 'LEG_DEFINED' ? this.$t('betFreeze.overriddenByOperator') : this.$t('betFreeze.overriddenLeg');
    },

    // Odds of the freeze UI: the global filter returns values <= 1 untouched
    // ("1", "0.5"), a frozen or fair odd below 1 is shown with two decimals.
    formatFreezeOdd(value) {
      const n = parseFloat(value);
      if (!Number.isFinite(n)) return value;
      return n <= 1 ? n.toFixed(2) : this.$options.filters.odds_type_filter(n);
    },

    betFreezeFairInfo(legState) {
      return fairInfo(legState);
    },

    betFreezeCost(bet, legState) {
      return freezeCost(bet, legState);
    },
  },
};
